
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { JUSTIFY_CONTENT, MARGIN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, setHTMLElementContent, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { ColorHex, ColorRgb, buildRgbString, hexToRgb, isValidHexColor, rgbToHex } from "@cervval.js/common/src/tools/color.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { UID } from "@cervval.js/common/src/tools/type.tools";

export enum CheckboxMode {
    CHECKBOX = "checkbox",
    RADIO = "radio",
    SWITCH = "form-switch"
}

export enum CheckboxSize {
    SMALL = 0.75,
    NORMAL = 1,
    LARGE = 1.25
}

export type CheckboxValue = boolean | null;

export interface CheckboxOptions<UID extends string = string> {

    /** Default label */
    label: AcceptedHtmlElementContent;

    /** Default is not disabled */
    disabled: ValueOrFunction<boolean>;

    /** Default checkbox mode */
    mode: CheckboxMode;

    /**
     * Size of checkbox, accepts 3 types of values 
     * 
     * CheckboxSize:
     * SMALL: default bootstrap checkbox size (eq:1)
     * NORMAL: x1.5 size from normal
     * LARGE: x2 size from normal
     * 
     * Custom number scale multiplier:
     * number: custom multiplier size from original size
     * 
     * Custom string css dimension:
     * string: css dimension value (px, em, vh, vw, etc.)
     * 
     * ! 'string' don't support "%" dimension value !
     */
    size: CheckboxSize | number | "SMALL" | "NORMAL" | "LARGE" | string;

    /**
     * Default size of checkbox (base dimension to apply sizing factors, in px)
     */
    defaultSize: number;

    /** Enable checkbox toggle click on label */
    enableLabelClick: boolean;

    /** Set label position, after or before checkbox */
    labelPosition: "after" | "before";

    /** Checkbox color (rgb & hexadecimal accepted) */
    color?: ColorHex | ColorRgb;

    /** Enables a third state for checkbox : Indeterminate */
    enableIndeterminate?: boolean;

    /**
     * Checkbox horizontal alignment
     * @default JUSTIFY_CONTENT.CENTER
     */
    checkboxHorizontalAlignment?: JUSTIFY_CONTENT;

    /**
     * Uid of checkbox, will set "ref" attribute of checkbox
     */
    uid?: UID;

    /** Default aria-label */
    ariaLabel?: string;
}

export const defaultCheckboxOptions: CheckboxOptions = {

    /** Default label */
    label: "",

    /** Default is not disabled */
    disabled: false,

    /** Default checkbox mode */
    mode: CheckboxMode.CHECKBOX,

    /**
     * Size of checkbox, accepts 3 types of values 
     * 
     * CheckboxSize:
     * SMALL: default bootstrap checkbox size (eq:1)
     * NORMAL: x1.5 size from normal
     * LARGE: x2 size from normal
     * 
     * Custom number scale multiplier:
     * number: custom multiplier size from original size
     * 
     * Custom string css dimension:
     * string: css dimension value (px, em, vh, vw, etc.)
     * 
     * ! 'string' don't support "%" dimension value !
     */
    size: CheckboxSize.NORMAL,

    /**
     * Default size of checkbox (base dimension to apply sizing factors, in px)
     */
    defaultSize: 20,

    /** Enable checkbox toggle click on label */
    enableLabelClick: true,

    /** Set label position, after or before checkbox */
    labelPosition: "after"

};

// Css classes
const LEFT_LABEL = "left-label";
const LABEL_MARGIN_START = MARGIN.MS_2;
const CLICK_ENABLE = "click-enable";
const CUSTOM_COLOR = "custom-color";

// Css variables
const VAR_CHECKBOX_COLOR = "--checkbox-color";
const VAR_CHECKBOX_COLOR_SHADOW = "--checkbox-color-shadow";
const VAR_CHECKBOX_COLOR_FOCUS_NOT_CHECKED = "--checkbox-color-focus-not-checked";
const VAR_CHECKBOX_COLOR_ACTIVE = "--checkbox-color-active";
const VAR_CHECKBOX_COLOR_SWITCH_URL = "--checkbox-color-switch-url";

@CustomElement({
    name: "cjs-checkbox",
    style: require("./checkbox.scss"),
    template: require("./checkbox.html")
})
export class CjsCheckbox<UIDValue extends UID = UID> extends WebComponent {

    /** 
     * Component HTML elements
     */
    @TypedHtmlElements()
    public readonly components!: {
        formCheckContainer: HTMLDivElement,
        formCheckInput: HTMLInputElement,
        formCheckLabel: HTMLLabelElement
    };

    @TypedWebComponentOptions(defaultCheckboxOptions)
    public readonly options!: WebComponentOptions<CheckboxOptions<UIDValue>>;

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** Checkbox value */
    protected _value: CheckboxValue = false;

    /** On toggle is triggered */
    @Event(NoBubblesEventInit)
    public toggleEvent!: EventHandler;

    /** On checkbox is checked */
    @Event(NoBubblesEventInit)
    public checkEvent!: EventHandler;

    /** On checkbox is unchecked */
    @Event(NoBubblesEventInit)
    public uncheckEvent!: EventHandler;

    /** On checkbox is set to indeterminate */
    @Event(NoBubblesEventInit)
    public indeterminateEvent!: EventHandler;

    constructor(options?: Partial<CheckboxOptions<UIDValue>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** 
     * When component is initialized 
     */
    public override init(): void {

        // Init listeners
        this._initListeners();

    }

    /**
     * Init component listeners
     */
    protected _initListeners(): void {
        this.components.formCheckInput.addEventListener("change", () => {
            // Set value
            this._value = this.components.formCheckInput.checked;

            // Trigger events
            this._dispatchChangeEvents();
        });
    }

    /**
     * Return checkbox value
     */
    public getValue(): CheckboxValue {
        return this._value;
    }

    /**
     * Return checkbox value as boolean
     * If value === null (undetermined) => return false
     */
    public getBooleanValue(): boolean {
        return Boolean(this.getValue());
    }

    /**
     * Set value to checkbox
     *
     * @param value Value to set
     * @param dispatchEvents change events will be dispatched if true and value changed
     */
    public setValue(value: CheckboxValue, dispatchEvents = true): void {
        if (this._value !== value) {
            this._value = value;
            this._refreshValue();
            if (dispatchEvents) {
                this._dispatchChangeEvents();
            }
        }
    }

    /**
     * Set input checked
     */
    public on(dispatchEvents = true): void {
        this.setValue(true, dispatchEvents);
    }

    /**
     * Set input unchecked
     */
    public off(dispatchEvents = true): void {
        this.setValue(false, dispatchEvents);
    }

    /**
     * Set input indeterminate
     * @param dispatchEvents 
     */
    public indeterminate(dispatchEvents = true): void {
        this.setValue(null, dispatchEvents);
    }

    /**
     * Toggle value
     */
    public toggle(dispatchEvents = true): void {
        this.setValue(!this._value, dispatchEvents);
    }

    /**
     * Dispatch onToggle and onCheck or onUncheck depending on current value
     */
    private _dispatchChangeEvents(): void {
        this.toggleEvent.emit();

        if (this._value) {
            this.checkEvent.emit();
        } else if (this._value === false) {
            this.uncheckEvent.emit();
        } else {
            this.indeterminateEvent.emit();
        }
    }

    /** 
     * Component refresh 
     */
    public override refresh(): void {

        // Set checkbox mode
        this._refreshMode();

        // Set checkbox label
        this._refreshLabel();

        // Set checkbox aria-label
        this._refreshAriaLabel();

        // Update disabled attribute
        this._refreshDisabled();

        // Refresh checkbox size
        this._refreshSize();

        // Refresh checkbox color
        this._refreshColor();

        // Update checkbox value display
        this._refreshValue();

        // Update ref attribute 
        this._refreshUid();
    }

    /**
     * Set component mode
     */
    protected _refreshMode(): void {

        // Set mode only if needed and if component wasConnected
        if (this.components.formCheckInput.type !== this.options.mode) {

            // Set new mode
            this.components.formCheckInput.type = CheckboxMode.CHECKBOX;
            this.components.formCheckContainer.classList.remove(CheckboxMode.SWITCH);

            switch (this.options.mode) {
                case CheckboxMode.RADIO:
                    this.components.formCheckInput.type = CheckboxMode.RADIO;
                    break;
                case CheckboxMode.SWITCH:
                    this.components.formCheckContainer.classList.add(CheckboxMode.SWITCH);
                    break;
                default:
                    break;
            }
        }

    }

    /**
     * Refresh label of checkbox
     */
    protected _refreshLabel(): void {
        const isLabelOptionDefined = this.options.label !== "" && this.options.label != null;

        // Add margin if label is defined
        toggleClass(this.components.formCheckLabel, isLabelOptionDefined, LABEL_MARGIN_START);

        // If label isn't defined, center checkbox in component
        toggleClass(this.components.formCheckContainer, !isLabelOptionDefined, this.options.checkboxHorizontalAlignment ?? JUSTIFY_CONTENT.CENTER);

        // Set in dom
        setHTMLElementContent(this.components.formCheckLabel, this.options.label);

        // Set label position (refresh don't need to be handled, accept than label position is never updated)
        if (this.options.labelPosition === "before") {
            this.components.formCheckContainer.prepend(this.components.formCheckLabel);
            this.components.formCheckContainer.classList.add(LEFT_LABEL);
            this.components.formCheckLabel.classList.remove(LABEL_MARGIN_START);
        }


        // Add id / for to enable click on label
        this.components.formCheckInput.id = "checkbox" + this.componentNumber;
        toggleAttribute(this.components.formCheckLabel, this.options.enableLabelClick, "for", this.components.formCheckInput.id);
    }

    /**
     * Refresh aria-label of checkbox
     */
    protected _refreshAriaLabel(): void {
        this.components.formCheckInput.ariaLabel = this.options.ariaLabel ?? "";
    }

    /**
     * Update checkbox value
     */
    protected _refreshValue(): void {
        if (this._value != null) {
            this.components.formCheckInput.checked = this._value;
            this.components.formCheckInput.indeterminate = false;
        } else if (this.options.enableIndeterminate) {
            this.components.formCheckInput.indeterminate = true;
        }
    }

    /**
     * Update disabled attribute
     */
    protected _refreshDisabled(): void {
        this.components.formCheckInput.disabled = this.disabled;

        // Remove click enable class on label
        if (this.disabled) {
            this.components.formCheckLabel.classList.remove(CLICK_ENABLE);
        } else {
            this.components.formCheckLabel.classList.add(CLICK_ENABLE);
        }
    }

    /** Refresh checkbox size, based on options.size */
    protected _refreshSize(): void {

        let transformScale = 1;
        if (typeof this.options.size === "number") {
            // number factor
            transformScale = this.options.size;
        } else if (typeof this.options.size === "string") {
            const sizeOption = getProp<number>(CheckboxSize, this.options.size);
            if (sizeOption != null) {
                // From enum
                transformScale = sizeOption;
            } else {

                // Custom size defined (string type)  (ex: 4em / 20px / 1vh / etc.)
                const dimensionType = this.options.size.slice(-2); // (px / em / vw / etc.)

                // Set the height
                this.components.formCheckInput.style.height = this.options.size;

                // If switch mode, multiply dimension given by 2 for width (switch is larger than taller)
                if (this.options.mode === CheckboxMode.SWITCH) {
                    const widthValue = Number(this.options.size.slice(0, -2)) * 2;

                    this.components.formCheckInput.style.width = widthValue + dimensionType;
                } else { // Set the width given in options
                    this.components.formCheckInput.style.width = this.options.size;
                }
            }
        }


        // Apply scale factor
        this.components.formCheckInput.style.height = transformScale * this.options.defaultSize + "px";

        if (this.options.mode === CheckboxMode.SWITCH) {
            this.components.formCheckInput.style.width = transformScale * this.options.defaultSize * 2 + "px";
        } else {
            this.components.formCheckInput.style.width = transformScale * this.options.defaultSize + "px";
        }

    }

    /** Refresh checkbox color, based on options.color */
    protected _refreshColor(): void {

        let color = this.options.color;
        if (color == null) { return; }

        let hexaColor: ColorHex | undefined;
        if (!isValidHexColor(color)) {
            // Try to auto fix color value
            const hexValue = rgbToHex(color);
            if (hexValue != null) {
                color = hexaColor;
                this.options.setWithoutRefresh({ color });
            }
        } else {
            hexaColor = color;
        }

        // Apply if given color is valid
        if (hexaColor && isValidHexColor(hexaColor)) { // TODO else remove CUSTOM_COLOR...

            // Add class to enable custom color to checkbox
            this.components.formCheckContainer.classList.add(CUSTOM_COLOR);

            // Set main color
            this.components.formCheckInput.style.setProperty(VAR_CHECKBOX_COLOR, hexaColor);

            // Set main color, with 0.25 alpha opacity (3F <=> 25% in hex)
            this.components.formCheckInput.style.setProperty(VAR_CHECKBOX_COLOR_SHADOW, hexaColor + "3F");

            // Change dot color if checkbox is a switch
            if (this.options.mode === CheckboxMode.SWITCH) {

                // Url value for dot icon, MDN standard svg, set color in fill attribute
                const urlValue = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23${hexaColor.substring(1)}'/%3e%3c/svg%3e")`;
                this.components.formCheckInput.style.setProperty(VAR_CHECKBOX_COLOR_SWITCH_URL, urlValue);
            }

            // Convert color to rgb, to apply variants
            const rgbColor = hexToRgb(hexaColor);

            if (rgbColor) {// If rgb color is valid

                /** ## VAR_CHECKBOX_COLOR_FOCUS_NOT_CHECKED ## */
                // Offset value for focus & notCheck state
                const focusNotCheckColorOffset = 51;

                // If {color + value > 255, reduce color value to 255}
                const red = rgbColor.r + focusNotCheckColorOffset > 255 ? 255 : rgbColor.r + focusNotCheckColorOffset;
                const green = rgbColor.g + focusNotCheckColorOffset > 255 ? 255 : rgbColor.g + focusNotCheckColorOffset;
                const blue = rgbColor.b + focusNotCheckColorOffset > 255 ? 255 : rgbColor.b + focusNotCheckColorOffset;

                // Create new rgb string
                const focusNotCheckRgbColor = buildRgbString({ r: red, g: green, b: blue });
                const hexValue = rgbToHex(focusNotCheckRgbColor);

                if (hexValue != null) {

                    // Set property
                    this.components.formCheckInput.style.setProperty(VAR_CHECKBOX_COLOR_FOCUS_NOT_CHECKED, hexValue);
                    /** ## VAR_CHECKBOX_COLOR_ACTIVE ## */
                    // Retrieve the most important color in rgb values
                    const mainColor = Math.max(rgbColor.r, rgbColor.g, rgbColor.b);

                    // Define lighten color
                    const first = 245;
                    const snd = 221;
                    const third = 214;

                    // Define colors from main color
                    // make variant circle based on r => g => b => r. (if r main, snd is g, third is b, if g main, snd is b, third is r, etc.)
                    const redActive = rgbColor.r === mainColor ? first : rgbColor.b === mainColor ? snd : third;
                    const greenActive = rgbColor.g === mainColor ? first : rgbColor.r === mainColor ? snd : third;
                    const blueActive = rgbColor.b === mainColor ? first : rgbColor.g === mainColor ? snd : third;

                    // Create new rgb string
                    const activeRgbColor = buildRgbString({ r: redActive, g: greenActive, b: blueActive });

                    // Set property
                    this.components.formCheckInput.style.setProperty(VAR_CHECKBOX_COLOR_ACTIVE, "#" + rgbToHex(activeRgbColor));
                }
            }
        }
    }

    /**
     * Refresh the uid option 
     */
    protected _refreshUid(): void {
        toggleAttribute(this.components.formCheckInput, Boolean(this.options.uid), "uid", this.options.uid ?? "");
    }
}